import { apiClient } from '@/api/client';

// In-memory cache for auth/me — lives for the entire browser session.
// Invalidated explicitly on logout, clinic switch, or profile update.
let meCache: {
  user: UserInfo;
  permissions: Permission[];
  role: string;
} | null = null;

// One request at a time per read: as the app starts, the session check and
// the clinic switcher both ask for the clinic list, and main.tsx sends the
// session check's reads before the language file has loaded. A caller that
// asks while the same read is on its way waits for that one.
const inFlight = new Map<string, Promise<unknown>>();
// Bumped on invalidation, so a read sent before it neither fills the cache
// nor is shared with a caller that asks after it.
let generation = 0;
// Reads started before anyone asked (startSessionReads). The first caller
// takes the request, on its way or already answered, instead of sending
// another.
const early = new Map<string, Promise<unknown>>();
function shared<T>(key: string, run: () => Promise<T>): Promise<T> {
  const started = early.get(key) as Promise<T> | undefined;
  if (started) {
    early.delete(key);
    return started;
  }
  const pending = inFlight.get(key) as Promise<T> | undefined;
  if (pending) return pending;
  const request = run().finally(() => {
    if (inFlight.get(key) === request) inFlight.delete(key);
  });
  inFlight.set(key, request);
  return request;
}

export function invalidateMeCache() {
  meCache = null;
  generation++;
  inFlight.clear();
  early.clear();
}

/**
 * Sends the session check's two reads (AuthContext's) now, ahead of the
 * check itself, which only runs once React has rendered. A read that fails
 * is not handed on: the check then sends its own.
 */
export function startSessionReads() {
  const reads: Array<[string, () => Promise<unknown>]> = [
    ['me|false', () => authApi.getMe(false, true)],
    ['clinics|false', () => authApi.getUserClinics(false, true)],
  ];
  for (const [key, read] of reads) {
    const request = read();
    early.set(key, request);
    request.catch(() => {
      if (early.get(key) === request) early.delete(key);
    });
  }
}
import type {
  LoginCredentials,
  Clinic,
  UserInfo,
  Permission,
} from '@/types/auth.types';

interface AuthResponse {
  user: {
    id: string;
    email: string;
    name: string;
    isEmailVerified: boolean;
  };
}

export const authApi = {
  // Register
  register: async (userData: {
    name: string;
    email: string;
    password: string;
  }) => {
    const response = await apiClient.post<AuthResponse>(
      '/auth/register',
      userData,
    );
    return response.data;
  },

  // Login
  login: async (credentials: Omit<LoginCredentials, 'rememberMe'>) => {
    const response = await apiClient.post<AuthResponse>(
      '/auth/login',
      credentials,
    );
    return response.data;
  },

  // Refresh token
  refreshToken: async () => {
    const response = await apiClient.get<{ success: boolean }>(
      '/auth/refresh-token',
    );
    return response.data;
  },

  // Logout
  logout: async () => {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },

  // Send OTP for email verification
  sendOtp: async () => {
    const response = await apiClient.get('/auth/send-otp');
    return response.data;
  },

  // Verify OTP
  verifyOtp: async (otp: string) => {
    const response = await apiClient.post('/auth/verify-otp', { otp });
    return response.data;
  },

  // Get user clinics
  getUserClinics: (skipRefresh = false, allowRefreshOnPublic = false) =>
    shared(`clinics|${skipRefresh}`, async () => {
      const response = await apiClient.get<Clinic[]>('/clinics/userClinics', {
        ...(skipRefresh && ({ _skipRefresh: true } as object)),
        ...(allowRefreshOnPublic &&
          ({ _allowRefreshOnPublic: true } as object)),
      });
      return response.data;
    }),

  // Select clinic — re-issues JWT with clinic claim embedded
  selectClinic: async (clinicId: string) => {
    const response = await apiClient.post<{ success: boolean }>(
      '/auth/select-clinic',
      { clinicId },
    );
    return response.data;
  },

  // Get current user info + permissions (reads clinic from JWT).
  // Cached in memory for the browser session — call invalidateMeCache() when
  // data must be fresh (logout, clinic switch, profile update).
  getMe: async (skipRefresh = false, allowRefreshOnPublic = false) => {
    if (meCache && !skipRefresh) return meCache;
    return shared(`me|${skipRefresh}`, async () => {
      const sentIn = generation;
      const response = await apiClient.get<{
        user: UserInfo;
        permissions: Permission[];
        role: string;
      }>('/auth/me', {
        ...(skipRefresh && ({ _skipRefresh: true } as object)),
        ...(allowRefreshOnPublic &&
          ({ _allowRefreshOnPublic: true } as object)),
      });
      if (sentIn === generation) meCache = response.data;
      return response.data;
    });
  },

  // Forgot password - request reset link
  forgotPassword: async (email: string) => {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  },

  // Reset password with token
  resetPassword: async (token: string, newPassword: string) => {
    const response = await apiClient.post('/auth/reset-password', {
      token,
      newPassword,
    });
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await apiClient.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  // Update current user profile
  updateProfile: async (data: {
    name?: string;
    specialty?: string;
    photoUrl?: string;
  }) => {
    const response = await apiClient.patch('/auth/profile', data);
    return response.data;
  },

  // Get presigned URL for profile photo upload
  getProfilePhotoUploadUrl: async (fileName: string, mimeType: string) => {
    const response = await apiClient.post<{ uploadUrl: string; s3Key: string }>(
      '/auth/profile/photo-upload-url',
      { fileName, mimeType },
    );
    return response.data;
  },

  // Check invitation validity and user existence
  checkInvitation: async (token: string) => {
    const response = await apiClient.post('/invitations/check', { token });
    return response.data;
  },

  // Register with invitation (combined endpoint)
  registerWithInvitation: async (userData: {
    name: string;
    password: string;
    token: string;
  }) => {
    const response = await apiClient.post('/invitations/register', userData);
    return response.data;
  },
};
